import { useState, useEffect, useRef } from 'react';
import { addDays, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import api from '../../services/api';
import MyRoute from './MyRoute';
import Modal from '../UI/Modal';

export default function MobilePlanning() {
    const [currentDay, setCurrentDay] = useState(new Date());
    const [deliveries, setDeliveries] = useState([]);
    const [dayInfo, setDayInfo] = useState(null);
    const [teamLocations, setTeamLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [filtreLibreur, setFiltreLibreur] = useState('');
    const [showMyRoute, setShowMyRoute] = useState(false);
    const [selectedDelivery, setSelectedDelivery] = useState(null);

    const [editNotes, setEditNotes] = useState('');
    const [editDate, setEditDate] = useState('');
    const [editConsigneHoraire, setEditConsigneHoraire] = useState('');
    const [saving, setSaving] = useState(false);
    const [editLivreur, setEditLivreur] = useState('');

    const containerRef = useRef(null);

    const dateStr = format(currentDay, 'yyyy-MM-dd');

    const typeBorderColors = {
        'standard':       null,
        'recup':          '#f59e0b',
        'installation':   '#ef4444',
        'envoi_tnt':      '#8b5cf6',
        'envoi_postal':   '#06b6d4',
        'visite_patient': '#10b981',
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentDay]);

    useEffect(() => {
        if (selectedDelivery) {
            setEditLivreur(selectedDelivery.livreur?.['@id'] || '');
            setEditNotes(selectedDelivery.notes || '');
            setEditDate(selectedDelivery.date ? selectedDelivery.date.substring(0, 10) : '');
            setEditConsigneHoraire(selectedDelivery.consigneHoraire || '');
        }
    }, [selectedDelivery]);

    // Swipe horizontal — listeners natifs DOM (contourne le système synthétique React)
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        let startX = null;
        let startY = null;
        let handled = false;

        const onTouchStart = (e) => {
            if (selectedDelivery) return;
            startX = e.touches[0].clientX;
            startY = e.touches[0].clientY;
            handled = false;
        };

        const onTouchMove = (e) => {
            if (startX === null || handled || selectedDelivery) return;
            const deltaX = e.touches[0].clientX - startX;
            const deltaY = e.touches[0].clientY - startY;
            const SEUIL = 60;

            if (Math.abs(deltaX) > SEUIL && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
                if (deltaX > 0) {
                    setCurrentDay(prev => addDays(prev, -1));
                } else {
                    setCurrentDay(prev => addDays(prev, 1));
                }
                handled = true;
            }
        };

        const onTouchEnd = () => {
            startX = null;
            startY = null;
            handled = false;
        };

        container.addEventListener('touchstart', onTouchStart, { passive: true });
        container.addEventListener('touchmove', onTouchMove, { passive: true });
        container.addEventListener('touchend', onTouchEnd, { passive: true });

        return () => {
            container.removeEventListener('touchstart', onTouchStart);
            container.removeEventListener('touchmove', onTouchMove);
            container.removeEventListener('touchend', onTouchEnd);
        };
    }, [selectedDelivery]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [deliveriesRes, dayInfoRes, locationsRes, usersRes] = await Promise.all([
                api.get(`/deliveries?date[after]=${dateStr}&date[before]=${dateStr}`),
                api.get(`/day_infos?date[after]=${dateStr}&date[before]=${dateStr}`),
                api.get(`/team_locations?date[after]=${dateStr}&date[before]=${dateStr}`),
                api.get('/users'),
            ]);
            setDeliveries(deliveriesRes.data.member || []);
            setDayInfo(dayInfoRes.data.member?.[0] || null);
            setTeamLocations(locationsRes.data.member || []);
            setUsers(usersRes.data.member || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!selectedDelivery) return;
        setSaving(true);
        try {
            await api.patch(`/deliveries/${selectedDelivery.id}`, {
                notes: editNotes || null,
                date: editDate || null,
                consigneHoraire: editConsigneHoraire || null,
                livreur: editLivreur || null,
            }, {
                headers: { 'Content-Type': 'application/merge-patch+json' },
            });

            setSelectedDelivery(null);
            loadData();
        } catch (err) {
            console.error('Erreur sauvegarde', err);
        } finally {
            setSaving(false);
        }
    };

    const sortDeliveries = (list) => list.sort((a, b) => {
        return (a.livreur?.nom || '').localeCompare(b.livreur?.nom || '');
    });

    const matin = sortDeliveries(deliveries.filter(d =>
        d.periode === 'matin' &&
        (!filtreLibreur || d.livreur?.['@id'] === filtreLibreur) &&
        d.patient?.statutPatient !== 'ftt' &&
        d.patient?.statutPatient !== 'en_pause' &&
        d.patient?.statutPatient !== 'hospitalise'
    ));
    const aprem = sortDeliveries(deliveries.filter(d =>
        d.periode === 'aprem' &&
        (!filtreLibreur || d.livreur?.['@id'] === filtreLibreur) &&
        d.patient?.statutPatient !== 'ftt' &&
        d.patient?.statutPatient !== 'en_pause' &&
        d.patient?.statutPatient !== 'hospitalise'
    ));

    const statutPrepIcon = {
        'a_contacter': '?',
        'confirme': '●',
        'prepare': '✓',
        'annule': '✕',
    };

    const statutPrepColor = {
        'a_contacter': '#d97706',
        'confirme': '#2563eb',
        'prepare': '#16a34a',
        'annule': '#dc2626',
    };

    const pathologieColors = {
        'nutrition':  '#16a34a',
        'perfusion':  '#2563eb',
        'parkinson':  '#1a1a2e',
    };

    const chuColors = {
        'nancy':       { bg: '#fef9c3', border: '#eab308' },
        'epinal':      { bg: '#dbeafe', border: '#3b82f6' },
        'bar_le_duc':  { bg: '#dcfce7', border: '#22c55e' },
        'neufchateau': { bg: '#fee2e2', border: '#ef4444' },
    };

    const DeliveryRow = ({ delivery }) => {
        const statutPatient = delivery.patient?.statutPatient;
        const isAttenued = statutPatient === 'en_pause' || statutPatient === 'hospitalise';
        const pathologieColor = isAttenued
            ? '#b0b8c1'
            : (pathologieColors[delivery.patient?.pathologie] || '#1a1a2e');
        const chu = delivery.patient?.chu;
        const chuStyle = chuColors[chu] || { bg: '#fef3c7', border: '#f59e0b' };
        const typeBorder = typeBorderColors[delivery.typelivraison];

        return (
            <div
                onClick={() => setSelectedDelivery(delivery)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 12px',
                    borderBottom: '1px solid #f0f0f0',
                    borderLeft: typeBorder ? `4px solid ${typeBorder}` : '4px solid transparent',
                    cursor: 'pointer',
                    backgroundColor: delivery.reportPending ? '#fffbeb' : 'white',
                }}
            >
                <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '4px',
                    backgroundColor: '#f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: delivery.statutPrep === 'prepare' ? '900' : '400',
                    color: statutPrepColor[delivery.statutPrep] || '#d97706',
                    flexShrink: 0,
                }}>
                    {statutPrepIcon[delivery.statutPrep] || '?'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: pathologieColor,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        flexWrap: 'wrap'
                    }}>
                        {delivery.imuno && (
                            <span
                                title={`Immunosuppresseur — CHU ${chu || 'non défini'}`}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '16px',
                                    height: '16px',
                                    borderRadius: '50%',
                                    backgroundColor: isAttenued ? '#e2e8f0' : chuStyle.bg,
                                    border: `1.5px solid ${isAttenued ? '#94a3b8' : chuStyle.border}`,
                                    fontSize: '9px',
                                    filter: isAttenued ? 'grayscale(1)' : 'none',
                                    flexShrink: 0,
                                }}
                            >⭐</span>
                        )}
                        {delivery.patient?.nom}
                        <span style={{ fontWeight: '400', color: isAttenued ? '#b0b8c1' : '#888', fontSize: '12px' }}>
                            {' '}({delivery.patient?.ville})
                        </span>
                        {delivery.reportPending && (
                            <span title="Compte rendu à faire" style={{ fontSize: '11px' }}>📋</span>
                        )}
                    </div>
                    {delivery.consigneHoraire && (
                        <div style={{
                            display: 'inline-block',
                            fontSize: '11px',
                            fontWeight: '600',
                            color: '#d97706',
                            backgroundColor: '#fef3c7',
                            padding: '1px 6px',
                            borderRadius: '4px',
                            marginTop: '3px',
                        }}>
                            ⏰ {delivery.consigneHoraire}
                        </div>
                    )}
                    {delivery.notes && (
                        <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                            {delivery.notes}
                        </div>
                    )}
                </div>
                {delivery.livreur?.nom && (
                    <div style={{
                        fontSize: '11px',
                        fontWeight: '600',
                        color: '#4f46e5',
                        backgroundColor: '#ede9fe',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        flexShrink: 0,
                    }}>
                        {delivery.livreur.nom.split(' ')[0]}
                    </div>
                )}
                {delivery.livreur2?.nom && (
                    <div style={{
                        fontSize: '11px',
                        fontWeight: '600',
                        color: '#0891b2',
                        backgroundColor: '#e0f2fe',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        flexShrink: 0,
                    }}>
                        {delivery.livreur2.nom.split(' ')[0]}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div ref={containerRef} style={{ minHeight: '100vh', touchAction: 'pan-y' }}>
            {/* En-tête sticky */}
            <div style={{
                backgroundColor: 'white',
                borderBottom: '1px solid #f0f0f0',
                position: 'sticky',
                top: '0',
                zIndex: 10,
            }}>
                {/* Ligne 1 : navigation */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                }}>
                    <button
                        onClick={() => setCurrentDay(addDays(currentDay, -1))}
                        style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#4f46e5' }}
                    >◀</button>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a2e', textTransform: 'capitalize' }}>
                            {format(currentDay, 'EEEE', { locale: fr })}
                        </div>
                        <div style={{ fontSize: '13px', color: '#888' }}>
                            {format(currentDay, 'd MMMM yyyy', { locale: fr })}
                        </div>
                    </div>
                    <button
                        onClick={() => setCurrentDay(addDays(currentDay, 1))}
                        style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#4f46e5' }}
                    >▶</button>
                </div>
            </div>

            {/* Ligne 2 : filtre livreur */}
            <div style={{ padding: '0 16px 10px' }}>
                <select
                    value={filtreLibreur}
                    onChange={e => setFiltreLibreur(e.target.value)}
                    style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0',
                        fontSize: '14px',
                        backgroundColor: filtreLibreur ? '#ede9fe' : '#f8fafc',
                        color: filtreLibreur ? '#4f46e5' : '#666',
                    }}
                >
                    <option value="">👤 Tous les livreurs</option>
                    {users.map(u => (
                        <option key={u['@id']} value={u['@id']}>{u.nom}</option>
                    ))}
                </select>
                <button
                    onClick={() => setShowMyRoute(true)}
                    style={{
                        width: '100%',
                        padding: '10px',
                        backgroundColor: '#16a34a',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        marginTop: '8px',
                    }}
                >
                    🗺️ Mon trajet optimisé
                </button>

                {showMyRoute && <MyRoute onClose={() => setShowMyRoute(false)} />}
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Chargement...</div>
            ) : (
                <>
                    {/* Infos du jour */}
                    {(dayInfo?.astreinte || teamLocations.length > 0) && (
                        <div style={{
                            padding: '10px 16px',
                            backgroundColor: '#f8fafc',
                            borderBottom: '1px solid #f0f0f0'
                        }}>
                            {dayInfo?.astreinte && (
                                <div style={{ fontSize: '12px', fontWeight: '700', color: '#4f46e5', marginBottom: '4px' }}>
                                    ASTREINTE : {dayInfo.astreinte.nom}
                                </div>
                            )}
                            {teamLocations.map(loc => (
                                <div key={loc.id} style={{ fontSize: '12px', color: '#854d0e', fontWeight: '600' }}>
                                    {loc.personne} → {loc.lieu}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Matin */}
                    <div style={{ backgroundColor: 'white', marginBottom: '8px' }}>
                        <div style={{
                            padding: '8px 16px',
                            backgroundColor: '#f8fafc',
                            fontSize: '11px',
                            fontWeight: '700',
                            color: '#888',
                            textTransform: 'uppercase'
                        }}>
                            Matin ({matin.length})
                        </div>
                        {matin.length === 0 ? (
                            <div style={{ padding: '16px', color: '#aaa', fontSize: '13px', textAlign: 'center' }}>Aucune livraison</div>
                        ) : (
                            matin.map(d => <DeliveryRow key={d.id} delivery={d} />)
                        )}
                    </div>

                    {/* Après-midi */}
                    <div style={{ backgroundColor: 'white' }}>
                        <div style={{
                            padding: '8px 16px',
                            backgroundColor: '#f8fafc',
                            fontSize: '11px',
                            fontWeight: '700',
                            color: '#888',
                            textTransform: 'uppercase'
                        }}>
                            Après-midi ({aprem.length})
                        </div>
                        {aprem.length === 0 ? (
                            <div style={{ padding: '16px', color: '#aaa', fontSize: '13px', textAlign: 'center' }}>Aucune livraison</div>
                        ) : (
                            aprem.map(d => <DeliveryRow key={d.id} delivery={d} />)
                        )}
                    </div>
                </>
            )}

            {/* Modal mobile simplifiée — notes + consigne horaire */}
            {selectedDelivery && (
                <Modal
                    title={selectedDelivery.patient?.nom || 'Modifier'}
                    onClose={() => setSelectedDelivery(null)}
                >
                    <div style={{ padding: '4px 0' }}>
                        {/* Infos patient en lecture seule */}
                        <div style={{
                            backgroundColor: '#f8fafc',
                            borderRadius: '8px',
                            padding: '10px 14px',
                            marginBottom: '16px',
                            fontSize: '13px',
                            color: '#666',
                        }}>
                            <span style={{ fontWeight: '600', color: '#1a1a2e' }}>
                                {selectedDelivery.patient?.ville}
                            </span>
                            {selectedDelivery.patient?.adresse && (
                                <span> — {selectedDelivery.patient.adresse}</span>
                            )}
                            <div style={{ marginTop: '4px', fontSize: '12px' }}>
                                {selectedDelivery.periode === 'matin' ? '🌅 Matin' : '🌇 Après-midi'}
                                {selectedDelivery.livreur?.nom && ` · ${selectedDelivery.livreur.nom}`}
                            </div>
                        </div>

                        {/* Livreur */}
                        <label style={{ display: 'block', marginBottom: '12px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '6px' }}>
                                👤 Livreur
                            </div>
                            <select
                                value={editLivreur}
                                onChange={e => setEditLivreur(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    borderRadius: '8px',
                                    border: '1px solid #e0e0e0',
                                    fontSize: '16px',
                                    backgroundColor: 'white',
                                    boxSizing: 'border-box',
                                }}
                            >
                                <option value="">Sélectionner</option>
                                {users.map(u => (
                                    <option key={u['@id']} value={u['@id']}>{u.nom}</option>
                                ))}
                            </select>
                        </label>

                        {/* Date */}
                        <label style={{ display: 'block', marginBottom: '12px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '6px' }}>
                                📅 Date
                            </div>
                            <input
                                type="date"
                                value={editDate}
                                onChange={e => setEditDate(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    borderRadius: '8px',
                                    border: '1px solid #e0e0e0',
                                    fontSize: '16px',
                                    backgroundColor: 'white',
                                    boxSizing: 'border-box',
                                }}
                            />
                        </label>

                        {/* Consigne horaire */}
                        <label style={{ display: 'block', marginBottom: '12px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '6px' }}>
                                ⏰ Consigne horaire (optionnel)
                            </div>
                            <input
                                type="text"
                                value={editConsigneHoraire}
                                onChange={e => setEditConsigneHoraire(e.target.value)}
                                placeholder="Ex: avant 10h, entre 14h et 15h..."
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    borderRadius: '8px',
                                    border: '1px solid #e0e0e0',
                                    fontSize: '16px',
                                    backgroundColor: 'white',
                                    boxSizing: 'border-box',
                                }}
                            />
                        </label>

                        {/* Notes */}
                        <label style={{ display: 'block', marginBottom: '20px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '6px' }}>
                                📝 Notes
                            </div>
                            <textarea
                                value={editNotes}
                                onChange={e => setEditNotes(e.target.value)}
                                rows={3}
                                placeholder="Informations de passage, accès, consignes…"
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    borderRadius: '8px',
                                    border: '1px solid #e0e0e0',
                                    fontSize: '14px',
                                    resize: 'none',
                                    backgroundColor: 'white',
                                    boxSizing: 'border-box',
                                    fontFamily: 'inherit',
                                }}
                            />
                        </label>

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={() => setSelectedDelivery(null)}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    borderRadius: '8px',
                                    border: '1px solid #e0e0e0',
                                    backgroundColor: 'white',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    color: '#666',
                                }}
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                style={{
                                    flex: 2,
                                    padding: '12px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    backgroundColor: saving ? '#a5b4fc' : '#4f46e5',
                                    color: 'white',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    cursor: saving ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {saving ? 'Enregistrement…' : 'Enregistrer'}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}